import {
  useCallback,
  useEffect,
  useState
} from 'react';
import api, {
  formatApiError
} from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Loader2,
  Package,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
  Truck
} from 'lucide-react';
import {
  useConfirmDialog
} from '@/components/useConfirmDialog';

const categorias = [
  'Vestuario',
  'Acessorios',
  'Equipamentos',
  'Suplementos',
  'Outros'
];

const statusConfig = {
  ativo: {
    label: 'Ativo',
    classe:
      'bg-green-400/10 text-green-400 border-green-400/20'
  },
  rascunho: {
    label: 'Rascunho',
    classe:
      'bg-amber-400/10 text-amber-400 border-amber-400/20'
  },
  indisponivel: {
    label: 'Indisponível',
    classe:
      'bg-red-400/10 text-red-400 border-red-400/20'
  },
  arquivado: {
    label: 'Arquivado',
    classe:
      'bg-zinc-400/10 text-zinc-400 border-zinc-400/20'
  }
};

const initialForm = {
  sku: '',
  nome: '',
  descricao: '',
  categoria: '',
  preco: '',
  custo: '',
  estoque: '0',
  estoque_minimo: '0',
  status: 'rascunho',
  destaque: 'nao',
  prazo_entrega_tipo: 'sob_consulta',
  prazo_entrega_dias: '0'
};

const fmt = (valor) =>
  Number(valor || 0).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL'
    }
  );

export default function ProdutosPage() {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategoria, setFilterCategoria] =
    useState('todas');
  const [filterStatus, setFilterStatus] =
    useState('todos');
  const [ordenar, setOrdenar] =
    useState('recentes');
  const [dialogOpen, setDialogOpen] =
    useState(false);
  const [editing, setEditing] =
    useState(null);
  const [form, setForm] =
    useState(initialForm);
  const [imagem, setImagem] =
    useState(null);
  const [saving, setSaving] =
    useState(false);

  const {
    confirm,
    ConfirmDialog
  } = useConfirmDialog();

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (search.trim()) {
        params.set('busca', search.trim());
      }

      if (filterCategoria !== 'todas') {
        params.set(
          'categoria',
          filterCategoria
        );
      }

      if (filterStatus !== 'todos') {
        params.set(
          'status',
          filterStatus
        );
      }

      params.set('ordenar', ordenar);

      const resposta = await api.get(
        `/gestor/loja/produtos?${params.toString()}`
      );

      setProdutos(resposta.data || []);
    } catch {
      toast.error(
        'Erro ao carregar produtos.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    search,
    filterCategoria,
    filterStatus,
    ordenar
  ]);

  useEffect(() => {
    const timer = setTimeout(
      fetchData,
      250
    );

    return () => clearTimeout(timer);
  }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setImagem(null);
    setDialogOpen(true);
  };

  const openEdit = (produto) => {
    setEditing(produto);
    setImagem(null);

    setForm({
      sku: produto.sku || '',
      nome: produto.nome || '',
      descricao: produto.descricao || '',
      categoria: produto.categoria || '',
      preco: String(produto.preco ?? ''),
      custo: String(produto.custo ?? 0),
      estoque: String(produto.estoque ?? 0),
      estoque_minimo: String(
        produto.estoque_minimo ?? 0
      ),
      status: produto.status || 'rascunho',
      destaque: produto.destaque
        ? 'sim'
        : 'nao',
      prazo_entrega_tipo:
        produto.prazo_entrega_tipo ||
        'sob_consulta',
      prazo_entrega_dias: String(
        produto.prazo_entrega_dias ?? 0
      )
    });

    setDialogOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const preco = Number(form.preco);
    const custo = Number(form.custo || 0);
    const estoque = Number(form.estoque || 0);
    const minimo = Number(
      form.estoque_minimo || 0
    );

    if (!form.nome.trim()) {
      toast.error(
        'Informe o nome do produto.'
      );
      return;
    }

    if (
      !Number.isFinite(preco) ||
      preco <= 0
    ) {
      toast.error(
        'O preço deve ser positivo.'
      );
      return;
    }

    if (
      custo < 0 ||
      estoque < 0 ||
      minimo < 0
    ) {
      toast.error(
        'Custo e estoques não podem ser negativos.'
      );
      return;
    }

    if (
      ['dias_uteis', 'dias_corridos'].includes(
        form.prazo_entrega_tipo
      ) &&
      Number(form.prazo_entrega_dias) < 1
    ) {
      toast.error(
        'Informe a quantidade de dias do prazo.'
      );
      return;
    }

    setSaving(true);

    try {
      const dados = new FormData();

      Object.entries({
        sku: form.sku || '',
        nome: form.nome,
        descricao: form.descricao || '',
        categoria: form.categoria || '',
        preco: String(preco),
        custo: String(custo),
        estoque: String(
          Math.floor(estoque)
        ),
        estoque_minimo: String(
          Math.floor(minimo)
        ),
        status: form.status,
        destaque: String(
          form.destaque === 'sim'
        ),
        prazo_entrega_tipo:
          form.prazo_entrega_tipo,
        prazo_entrega_dias: String(
          Number(
            form.prazo_entrega_dias || 0
          )
        )
      }).forEach(([chave, valor]) => {
        dados.append(chave, valor);
      });

      if (imagem) {
        dados.append('imagem', imagem);
      }

      if (editing) {
        await api.put(
          `/gestor/loja/produtos/${editing.id}`,
          dados,
          {
            headers: {
              'Content-Type':
                'multipart/form-data'
            }
          }
        );

        toast.success(
          'Produto atualizado.'
        );
      } else {
        await api.post(
          '/gestor/loja/produtos',
          dados,
          {
            headers: {
              'Content-Type':
                'multipart/form-data'
            }
          }
        );

        toast.success('Produto criado.');
      }

      setDialogOpen(false);
      setEditing(null);
      setImagem(null);
      fetchData();
    } catch (erro) {
      toast.error(
        formatApiError(erro)
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (produto) => {
    const novoStatus =
      produto.status === 'ativo'
        ? 'rascunho'
        : 'ativo';

    try {
      await api.put(
        `/gestor/loja/produtos/${produto.id}`,
        {
          status: novoStatus
        }
      );

      fetchData();
    } catch (erro) {
      toast.error(
        formatApiError(erro)
      );
    }
  };

  const toggleDestaque = async (
    produto
  ) => {
    try {
      await api.put(
        `/gestor/loja/produtos/${produto.id}`,
        {
          destaque:
            !produto.destaque
        }
      );

      fetchData();
    } catch (erro) {
      toast.error(
        formatApiError(erro)
      );
    }
  };

  const handleDelete = async (
    produto
  ) => {
    const confirmado = await confirm(
      'Remover produto',
      (
        `Remover ${produto.nome}? ` +
        `Se houver pedidos vinculados, ` +
        `o produto será arquivado.`
      )
    );

    if (!confirmado) return;

    try {
      const resposta = await api.delete(
        `/gestor/loja/produtos/${produto.id}`
      );

      toast.success(
        resposta.data?.message ||
          'Produto removido.'
      );

      fetchData();
    } catch (erro) {
      toast.error(
        formatApiError(erro)
      );
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black font-['Barlow_Condensed'] uppercase text-white">
            Produtos
          </h1>
          <p className="text-sm text-zinc-500">
            Catálogo, estoque e prazos.
          </p>
        </div>

        <Button
          onClick={openCreate}
          className="bg-[var(--cor-primaria,#007AFF)]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            className="pl-10 bg-[#0A0A0A] border-[#27272A]"
            placeholder="Nome, SKU, categoria ou descrição..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />
        </div>

        <Select
          value={filterCategoria}
          onValueChange={setFilterCategoria}
        >
          <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">
              Todas as categorias
            </SelectItem>
            {categorias.map((categoria) => (
              <SelectItem
                key={categoria}
                value={categoria}
              >
                {categoria}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filterStatus}
          onValueChange={setFilterStatus}
        >
          <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">
              Todos os status
            </SelectItem>
            {Object.entries(statusConfig).map(
              ([valor, config]) => (
                <SelectItem
                  key={valor}
                  value={valor}
                >
                  {config.label}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      </div>

      <Select
        value={ordenar}
        onValueChange={setOrdenar}
      >
        <SelectTrigger className="w-full md:w-56 bg-[#0A0A0A] border-[#27272A]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="recentes">
            Mais recentes
          </SelectItem>
          <SelectItem value="nome-az">
            Nome A–Z
          </SelectItem>
          <SelectItem value="menor-preco">
            Menor preço
          </SelectItem>
          <SelectItem value="maior-preco">
            Maior preço
          </SelectItem>
          <SelectItem value="mais-vendidos">
            Mais vendidos
          </SelectItem>
          <SelectItem value="menor-estoque">
            Menor estoque
          </SelectItem>
          <SelectItem value="destaques">
            Destaques primeiro
          </SelectItem>
        </SelectContent>
      </Select>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A]">
                <TableHead />
                <TableHead>Produto</TableHead>
                <TableHead>Preço / custo</TableHead>
                <TableHead>Estoque</TableHead>
                <TableHead>Prazo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  Ações
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-10"
                  >
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : produtos.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-10 text-zinc-500"
                  >
                    Nenhum produto encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                produtos.map((produto) => {
                  const status =
                    statusConfig[produto.status] ||
                    statusConfig.rascunho;

                  return (
                    <TableRow
                      key={produto.id}
                      className="border-[#27272A]"
                    >
                      <TableCell>
                        <div className="w-12 h-12 rounded-lg bg-[#0A0A0A] overflow-hidden flex items-center justify-center">
                          {produto.imagem_url ? (
                            <img
                              src={produto.imagem_url}
                              alt={produto.nome}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package className="w-5 h-5 text-zinc-600" />
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">
                            {produto.nome}
                          </p>
                          {produto.destaque && (
                            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          )}
                        </div>
                        <p className="text-xs text-zinc-500">
                          {produto.sku} · {produto.categoria || 'Sem categoria'}
                        </p>
                      </TableCell>

                      <TableCell>
                        <p className="text-white font-bold">
                          {fmt(produto.preco)}
                        </p>
                        <p className="text-xs text-zinc-500">
                          Custo: {fmt(produto.custo)}
                        </p>
                        <p className="text-xs text-green-400">
                          Margem: {produto.margem_percentual}%
                        </p>
                      </TableCell>

                      <TableCell>
                        <p className={
                          produto.estoque_baixo
                            ? 'text-red-400 font-bold'
                            : 'text-white font-bold'
                        }>
                          {produto.estoque_disponivel} disponível
                        </p>
                        <p className="text-xs text-zinc-500">
                          Total: {produto.estoque} · Reservado: {produto.estoque_reservado}
                        </p>
                        {produto.estoque_baixo && (
                          <div className="flex items-center gap-1 text-xs text-red-400 mt-1">
                            <AlertTriangle className="w-3 h-3" />
                            Estoque baixo
                          </div>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-zinc-300">
                          <Truck className="w-4 h-4 text-[#007AFF]" />
                          {produto.prazo_entrega_texto}
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge className={status.classe}>
                          {status.label}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-right space-x-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            toggleDestaque(produto)
                          }
                          className={
                            produto.destaque
                              ? 'text-amber-400'
                              : 'text-zinc-500'
                          }
                        >
                          <Star className="w-4 h-4" />
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            toggleStatus(produto)
                          }
                        >
                          {produto.status === 'ativo' ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            openEdit(produto)
                          }
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            handleDelete(produto)
                          }
                          className="hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      >
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">
              {editing
                ? 'Editar Produto'
                : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input
                  value={form.nome}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      nome: e.target.value
                    })
                  }
                  className="bg-[#0A0A0A] border-[#27272A]"
                  required
                />
              </div>

              <div>
                <Label>SKU</Label>
                <Input
                  value={form.sku}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      sku: e.target.value
                    })
                  }
                  className="bg-[#0A0A0A] border-[#27272A]"
                  placeholder="Automático se vazio"
                />
              </div>
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={form.descricao}
                onChange={(e) =>
                  setForm({
                    ...form,
                    descricao: e.target.value
                  })
                }
                className="bg-[#0A0A0A] border-[#27272A]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select
                  value={form.categoria || 'none'}
                  onValueChange={(valor) =>
                    setForm({
                      ...form,
                      categoria:
                        valor === 'none'
                          ? ''
                          : valor
                    })
                  }
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      Sem categoria
                    </SelectItem>
                    {categorias.map((categoria) => (
                      <SelectItem
                        key={categoria}
                        value={categoria}
                      >
                        {categoria}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Preço</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.preco}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      preco: e.target.value
                    })
                  }
                  className="bg-[#0A0A0A] border-[#27272A]"
                  required
                />
              </div>

              <div>
                <Label>Custo</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.custo}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      custo: e.target.value
                    })
                  }
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Estoque atual</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.estoque}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      estoque: e.target.value
                    })
                  }
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>

              <div>
                <Label>Estoque mínimo</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.estoque_minimo}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      estoque_minimo:
                        e.target.value
                    })
                  }
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>
            </div>

            <div className="rounded-lg border border-[#27272A] p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#007AFF]" />
                <p className="font-bold text-white">
                  Prazo de entrega
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Tipo de prazo</Label>
                  <Select
                    value={form.prazo_entrega_tipo}
                    onValueChange={(valor) =>
                      setForm({
                        ...form,
                        prazo_entrega_tipo:
                          valor,
                        prazo_entrega_dias:
                          ['pronta_entrega', 'sob_consulta'].includes(
                            valor
                          )
                            ? '0'
                            : form.prazo_entrega_dias
                      })
                    }
                  >
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pronta_entrega">
                        Pronta entrega
                      </SelectItem>
                      <SelectItem value="dias_uteis">
                        Dias úteis
                      </SelectItem>
                      <SelectItem value="dias_corridos">
                        Dias corridos
                      </SelectItem>
                      <SelectItem value="sob_consulta">
                        Sob consulta
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Quantidade de dias</Label>
                  <Input
                    type="number"
                    min="1"
                    disabled={
                      ![
                        'dias_uteis',
                        'dias_corridos'
                      ].includes(
                        form.prazo_entrega_tipo
                      )
                    }
                    value={
                      form.prazo_entrega_dias
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        prazo_entrega_dias:
                          e.target.value
                      })
                    }
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(valor) =>
                    setForm({
                      ...form,
                      status: valor
                    })
                  }
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusConfig).map(
                      ([valor, config]) => (
                        <SelectItem
                          key={valor}
                          value={valor}
                        >
                          {config.label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Destaque</Label>
                <Select
                  value={form.destaque}
                  onValueChange={(valor) =>
                    setForm({
                      ...form,
                      destaque: valor
                    })
                  }
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nao">
                      Não
                    </SelectItem>
                    <SelectItem value="sim">
                      Sim
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Imagem</Label>
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) =>
                  setImagem(
                    e.target.files?.[0] ||
                    null
                  )
                }
                className="bg-[#0A0A0A] border-[#27272A]"
              />
              <p className="text-xs text-zinc-500 mt-1">
                JPG, PNG ou WebP. Máximo de 5 MB.
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setDialogOpen(false)
                }
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                disabled={saving}
                className="bg-[var(--cor-primaria,#007AFF)]"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editing ? (
                  'Salvar'
                ) : (
                  'Criar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
}
